import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from './lib/supabase.js';
import { durationMin, todayIso } from './lib/format.js';
import { computeGamification } from './lib/gamification.js';
import { currentMonthKey } from './lib/goals.js';
import { isOverdue } from './lib/deadlines.js';
import { computeMissingTimeDays } from './lib/missing-times.js';
import { extractMentionIds, mentionSnippet } from './lib/mentions.js';

const DataContext = createContext(null);

export function DataProvider({ session, children }) {
  const userId = session.user.id;

  const [profiles, setProfiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]); // [{project_id, user_id}]
  const [completions, setCompletions] = useState([]);        // project_completions rows
  const [absences, setAbsences] = useState([]);              // absences rows
  const [tasks, setTasks] = useState([]);                    // project tasks
  const [deadlineChanges, setDeadlineChanges] = useState([]); // deadline_changes rows
  const [feedback, setFeedback] = useState([]);              // feedback rows (to current user or all if admin)
  const [goals, setGoals] = useState([]);                    // customer_goals rows (one per customer)
  const [metrics, setMetrics] = useState([]);                // goal_metrics rows (criteria per goal)
  const [goalProgress, setGoalProgress] = useState([]);      // goal_progress rows (per metric)
  const [checkins, setCheckins] = useState([]);              // goal_checkins rows
  const [deadlineOverruns, setDeadlineOverruns] = useState([]); // deadline_overruns rows
  const [overrunsReady, setOverrunsReady] = useState(false);    // migration 19 applied?
  const [mentions, setMentions] = useState([]);                 // mentions rows (to current user or all if admin)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    const [pRes, cRes, prRes, teRes, pmRes, ccRes, abRes, tkRes, dcRes, fbRes, gRes, gmRes, gpRes, gcRes, doRes, mnRes] = await Promise.all([
      supabase.from('profiles').select('*').order('full_name'),
      supabase.from('customers').select('*').order('name'),
      supabase.from('projects').select('*').order('created_at', { ascending: false }),
      supabase.from('time_entries').select('*').order('entry_date', { ascending: false }).order('from_time', { ascending: true }),
      supabase.from('project_members').select('*'),
      supabase.from('project_completions').select('*'),
      supabase.from('absences').select('*').order('start_date', { ascending: false }),
      supabase.from('tasks').select('*').order('position', { ascending: true }),
      supabase.from('deadline_changes').select('*').order('changed_at', { ascending: false }),
      supabase.from('feedback').select('*').order('created_at', { ascending: false }),
      supabase.from('customer_goals').select('*'),
      supabase.from('goal_metrics').select('*').order('position', { ascending: true }),
      supabase.from('goal_progress').select('*').order('period_month', { ascending: true }),
      supabase.from('goal_checkins').select('*').order('created_at', { ascending: false }),
      supabase.from('deadline_overruns').select('*').order('acknowledged_at', { ascending: false }),
      supabase.from('mentions').select('*').order('created_at', { ascending: false }),
    ]);
    // Core tables: a failure here is fatal (app can't render without them).
    const firstError = pRes.error || cRes.error || prRes.error || teRes.error || pmRes.error || ccRes.error || abRes.error || tkRes.error || dcRes.error || fbRes.error;
    if (firstError) {
      setError(firstError.message);
    } else {
      setProfiles(pRes.data || []);
      setCustomers(cRes.data || []);
      setProjects(prRes.data || []);
      setTimeEntries(teRes.data || []);
      setProjectMembers(pmRes.data || []);
      setCompletions(ccRes.data || []);
      setAbsences(abRes.data || []);
      setTasks(tkRes.data || []);
      setDeadlineChanges(dcRes.data || []);
      setFeedback(fbRes.data || []);
      // Goal tables are optional: if migration 17 hasn't been applied yet, these
      // queries error — we degrade gracefully (no goals) instead of breaking the
      // whole app. Once the migration runs they light up on next load.
      if (gRes.error || gmRes.error || gpRes.error || gcRes.error) {
        console.warn('[goals] tables not available yet — run supabase/17, 18, 20 (goals) migrations', gRes.error?.message);
      }
      setGoals(gRes.data || []);
      setMetrics(gmRes.data || []);
      setGoalProgress(gpRes.data || []);
      setCheckins(gcRes.data || []);
      // deadline_overruns is optional too: if migration 19 hasn't run yet the
      // query errors and we simply show no overruns (no enforcement) until it does.
      if (doRes.error) {
        console.warn('[deadlines] deadline_overruns not available yet — run supabase/19_deadline_overruns.sql', doRes.error?.message);
      }
      setOverrunsReady(!doRes.error);
      setDeadlineOverruns(doRes.data || []);
      // mentions is optional too: if migration 23 hasn't run yet the query
      // errors and we simply show no mentions until it does.
      if (mnRes.error) {
        console.warn('[mentions] table not available yet — run supabase/23_mentions.sql', mnRes.error?.message);
      }
      setMentions(mnRes.data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- Derived data ---------------------------------------------------------
  const currentProfile = useMemo(
    () => profiles.find(p => p.id === userId) || null,
    [profiles, userId]
  );

  const isAdmin = currentProfile?.role === 'admin';

  const projectById = useCallback((id) => projects.find(p => p.id === id), [projects]);
  const customerById = useCallback((id) => customers.find(c => c.id === id), [customers]);
  const profileById = useCallback((id) => profiles.find(p => p.id === id), [profiles]);

  const membersByProject = useMemo(() => {
    const map = {};
    for (const { project_id, user_id } of projectMembers) {
      (map[project_id] ||= []).push(user_id);
    }
    return map;
  }, [projectMembers]);

  // Anyone formally on the project OR who logged time on it.
  const involvedByProject = useMemo(() => {
    const map = {};
    for (const { project_id, user_id } of projectMembers) {
      (map[project_id] ||= new Set()).add(user_id);
    }
    for (const te of timeEntries) {
      (map[te.project_id] ||= new Set()).add(te.user_id);
    }
    const flat = {};
    for (const k of Object.keys(map)) flat[k] = [...map[k]];
    return flat;
  }, [projectMembers, timeEntries]);

  const completionsByProject = useMemo(() => {
    const map = {};
    for (const c of completions) (map[c.project_id] ||= []).push(c);
    return map;
  }, [completions]);

  // Minutes used per project (across all users). Euro used approximated at project's hourly rate
  // (fallback to user rates if we later add project.hourly_rate). Support tasks
  // (project_id = null) are excluded — they don't count toward any project budget.
  const projectUsage = useMemo(() => {
    const usage = {};
    for (const te of timeEntries) {
      if (!te.project_id) continue;
      const min = durationMin(te.from_time, te.to_time);
      const p = profiles.find(pr => pr.id === te.user_id);
      const rate = Number(p?.hourly_rate ?? 120);
      const slot = (usage[te.project_id] ||= { minutes: 0, euro: 0 });
      slot.minutes += min;
      slot.euro += (min / 60) * rate;
    }
    return usage;
  }, [timeEntries, profiles]);

  const todaysEntries = useMemo(() => {
    const today = todayIso();
    return timeEntries.filter(te => te.entry_date === today && te.user_id === userId);
  }, [timeEntries, userId]);

  const gamification = useMemo(
    () => computeGamification(timeEntries.filter(te => te.user_id === userId)),
    [timeEntries, userId]
  );

  const gamificationByUser = useMemo(() => {
    const map = {};
    for (const p of profiles) {
      map[p.id] = computeGamification(timeEntries.filter(te => te.user_id === p.id));
    }
    return map;
  }, [profiles, timeEntries]);

  // Feedback addressed TO the current user (RLS already scopes admins to all,
  // members to their own — we still filter so an admin's bell only shows their
  // own incoming feedback, not feedback they sent to others).
  const myFeedback = useMemo(
    () => feedback.filter(f => f.to_user_id === userId),
    [feedback, userId]
  );
  const unreadFeedbackCount = useMemo(
    () => myFeedback.filter(f => !f.read_at).length,
    [myFeedback]
  );

  // @-Mentions addressed TO the current user (same filtering rationale as feedback).
  const myMentions = useMemo(
    () => mentions.filter(m => m.to_user_id === userId),
    [mentions, userId]
  );
  const unreadMentionsCount = useMemo(
    () => myMentions.filter(m => !m.read_at).length,
    [myMentions]
  );

  // --- Goals (Nordstern) ----------------------------------------------------
  // Goals live at the CUSTOMER level; a project inherits its customer's goal.
  const goalByCustomer = useMemo(() => {
    const map = {};
    for (const g of goals) map[g.customer_id] = g;
    return map;
  }, [goals]);

  // The goal that applies to a given project = its customer's goal (or null).
  const goalForProject = useCallback(
    (project) => (project?.customer_id ? goalByCustomer[project.customer_id] || null : null),
    [goalByCustomer]
  );

  // Members who must report on a customer goal = union of formal members across
  // ALL active projects of that customer.
  const goalMembersByCustomer = useMemo(() => {
    const map = {};
    for (const g of goals) {
      const set = new Set();
      for (const p of projects) {
        if (p.customer_id !== g.customer_id || p.status !== 'active') continue;
        for (const uid of (membersByProject[p.id] || [])) set.add(uid);
      }
      map[g.customer_id] = [...set];
    }
    return map;
  }, [goals, projects, membersByProject]);

  const metricsByGoal = useMemo(() => {
    const map = {};
    for (const m of metrics) (map[m.goal_id] ||= []).push(m);
    for (const k of Object.keys(map)) map[k].sort((a, b) => (a.position || 0) - (b.position || 0));
    return map;
  }, [metrics]);

  const progressByMetric = useMemo(() => {
    const map = {};
    for (const p of goalProgress) (map[p.metric_id] ||= []).push(p);
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.period_month.localeCompare(b.period_month));
    return map;
  }, [goalProgress]);

  const checkinsByGoal = useMemo(() => {
    const map = {};
    for (const c of checkins) (map[c.goal_id] ||= []).push(c);
    return map;
  }, [checkins]);

  // Goal check-ins the current user still owes for the current month: they're a
  // member of at least one active project of a customer that has a goal, and
  // have no check-in row for this month yet. One check-in per customer goal —
  // this is the "forced monthly reflection" trigger.
  const dueGoalCheckins = useMemo(() => {
    const mk = currentMonthKey();
    const res = [];
    for (const g of goals) {
      const members = goalMembersByCustomer[g.customer_id] || [];
      if (!members.includes(userId)) continue;
      const has = checkins.some(c => c.goal_id === g.id && c.user_id === userId && c.period_month === mk);
      if (!has) {
        const customer = customers.find(c => c.id === g.customer_id);
        if (customer) res.push({ goal: g, customer });
      }
    }
    return res;
  }, [goals, goalMembersByCustomer, checkins, userId, customers]);

  // --- Deadline overruns ----------------------------------------------------
  // All overrun statements grouped by project (newest first).
  const overrunsByProject = useMemo(() => {
    const map = {};
    for (const o of deadlineOverruns) (map[o.project_id] ||= []).push(o);
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (b.acknowledged_at || '').localeCompare(a.acknowledged_at || ''));
    }
    return map;
  }, [deadlineOverruns]);

  // Has the project's *current* (missed) deadline already been addressed?
  const overrunForCurrentDeadline = useCallback((project) => {
    if (!project?.deadline) return null;
    return deadlineOverruns.find(
      o => o.project_id === project.id && o.missed_deadline === project.deadline
    ) || null;
  }, [deadlineOverruns]);

  // Projects the current user OWNS (created) that have blown their deadline and
  // still owe a statement for that missed deadline. This is the forced-dialog
  // trigger. Sorted most-overdue first.
  const dueDeadlineOverruns = useMemo(() => {
    if (!overrunsReady) return []; // no table/RPC yet → don't trap the owner
    const today = todayIso();
    return projects
      .filter(p => p.created_by === userId && isOverdue(p, today))
      .filter(p => !overrunForCurrentDeadline(p))
      .sort((a, b) => (a.deadline || '').localeCompare(b.deadline || ''));
  }, [overrunsReady, projects, userId, overrunForCurrentDeadline]);

  // --- Missing-time enforcement ---------------------------------------------
  // Past workdays the current user skipped (no time entry, no absence). This is
  // the forced catch-up trigger — see lib/missing-times.js. Pure client-side,
  // no DB table needed; recomputes as soon as an entry or absence is added.
  const dueMissingTimeDays = useMemo(
    () => computeMissingTimeDays({
      userId,
      timeEntries,
      absences,
      profileCreatedAt: currentProfile?.created_at,
    }),
    [userId, timeEntries, absences, currentProfile?.created_at]
  );

  // --- Mutations ------------------------------------------------------------
  async function addTimeEntry({
    project_id = null, customer_id = null, kind = null,
    from_time, to_time, description = null,
    entry_date = todayIso(), billable = true,
  }) {
    // Default kind: project if project_id, otherwise support
    const resolvedKind = kind || (project_id ? 'project' : 'support');
    const { data, error } = await supabase.from('time_entries').insert({
      user_id: userId,
      project_id: project_id || null,
      customer_id: customer_id || null,
      kind: resolvedKind,
      from_time, to_time,
      description: description || null,
      entry_date,
      billable,
    }).select().single();
    if (error) throw error;
    setTimeEntries(xs => [data, ...xs]);
    return data;
  }

  async function updateTimeEntry(id, patch) {
    // Optimistic update so the UI reflects the change in the same render tick
    // as the caller's other state changes (e.g. drag-end clearing its preview).
    // Otherwise we get a one-frame flash of the pre-update state.
    let prev = null;
    setTimeEntries(xs => {
      prev = xs.find(e => e.id === id) || null;
      return xs.map(e => e.id === id ? { ...e, ...patch } : e);
    });
    try {
      const { data, error } = await supabase.from('time_entries').update(patch).eq('id', id).select().single();
      if (error) throw error;
      setTimeEntries(xs => xs.map(e => e.id === id ? data : e));
      return data;
    } catch (e) {
      // Roll back to the previous server-confirmed state on failure.
      if (prev) setTimeEntries(xs => xs.map(x => x.id === id ? prev : x));
      throw e;
    }
  }

  async function deleteTimeEntry(id) {
    const { error } = await supabase.from('time_entries').delete().eq('id', id);
    if (error) throw error;
    setTimeEntries(xs => xs.filter(e => e.id !== id));
  }

  // Kopiert einen Zeiteintrag an mehrere Kolleg:innen (unabhängige Kopien, je eigene
  // user_id). Läuft über die SECURITY-DEFINER-RPC share_time_entry (Migration 22),
  // weil RLS sonst nur eigene Einträge zulässt. Gibt die angelegten Kopien zurück.
  async function shareTimeEntry({
    user_ids, project_id = null, customer_id = null, kind = null,
    entry_date, from_time, to_time, description = null, billable = true,
  }) {
    const ids = (user_ids || []).filter(Boolean);
    if (ids.length === 0) return [];
    const { data, error } = await supabase.rpc('share_time_entry', {
      p_user_ids: ids,
      p_project_id: project_id || null,
      p_customer_id: customer_id || null,
      p_kind: kind || (project_id ? 'project' : 'support'),
      p_entry_date: entry_date,
      p_from_time: from_time,
      p_to_time: to_time,
      p_description: description || null,
      p_billable: billable,
    });
    if (error) {
      // RPC fehlt noch → Migration 22 nicht eingespielt: klare Meldung statt Postgres-Kauderwelsch.
      if (error.code === 'PGRST202' || /share_time_entry|schema cache|does not exist/i.test(error.message || '')) {
        throw new Error('Teilen ist noch nicht aktiv — Migration 22 (share_time_entry) muss in Supabase eingespielt werden.');
      }
      throw error;
    }
    const rows = data || [];
    if (rows.length > 0) setTimeEntries(xs => [...rows, ...xs]);
    return rows;
  }

  async function addCustomer(input) {
    const { data, error } = await supabase.from('customers').insert(input).select().single();
    if (error) throw error;
    setCustomers(xs => [...xs, data].sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }

  async function updateCustomer(id, patch) {
    const { data, error } = await supabase.from('customers').update(patch).eq('id', id).select().single();
    if (error) throw error;
    setCustomers(xs => xs.map(c => c.id === id ? data : c).sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }

  async function deleteCustomer(id) {
    const { error } = await supabase.from('customers').delete().eq('id', id);
    if (error) throw error;
    setCustomers(xs => xs.filter(c => c.id !== id));
    // projects referencing this customer now have customer_id = null (ON DELETE SET NULL)
    setProjects(xs => xs.map(p => p.customer_id === id ? { ...p, customer_id: null } : p));
  }

  async function addProject(input, memberIds = []) {
    const payload = { created_by: userId, ...input };
    const { data, error } = await supabase.from('projects').insert(payload).select().single();
    if (error) throw error;
    setProjects(xs => [data, ...xs]);
    if (memberIds.length > 0) {
      const rows = memberIds.map(user_id => ({ project_id: data.id, user_id }));
      const { error: memErr } = await supabase.from('project_members').insert(rows);
      if (memErr) throw memErr;
      setProjectMembers(xs => [...xs, ...rows]);
    }
    return data;
  }

  // Legt die Google-Drive-Ordnerstruktur für ein Projekt an (Edge Function hält
  // den Service-Account-Key). Idempotent: ist der Ordner schon da, gibt die
  // Funktion ihn nur zurück. Setzt den lokalen Status optimistisch auf 'pending',
  // damit die UI sofort reagiert, und schreibt das Ergebnis ins Projekt zurück.
  async function syncDriveFolders(projectId) {
    setProjects(xs => xs.map(p => p.id === projectId ? { ...p, drive_sync_status: 'pending' } : p));
    const { data, error } = await supabase.functions.invoke('create-project-folders', {
      body: { projectId },
    });
    // functions.invoke liefert bei HTTP-Fehler error + ggf. die Server-Message im Body.
    if (error || data?.error) {
      const msg = data?.error || error?.message || 'Drive-Ordner konnte nicht angelegt werden';
      setProjects(xs => xs.map(p => p.id === projectId ? { ...p, drive_sync_status: 'error', drive_sync_error: msg } : p));
      throw new Error(msg);
    }
    setProjects(xs => xs.map(p => p.id === projectId
      ? { ...p, drive_folder_id: data.folderId, drive_folder_url: data.url, drive_sync_status: 'created', drive_sync_error: null }
      : p));
    return data;
  }

  async function updateProject(id, patch) {
    const { data, error } = await supabase.from('projects').update(patch).eq('id', id).select().single();
    if (error) throw error;
    setProjects(xs => xs.map(p => p.id === id ? data : p));
    return data;
  }

  // Atomic deadline change: shifts the project's deadline AND records the
  // mandatory reason in deadline_changes. Throws 'deadline_reason_required'
  // if reason is missing.
  async function changeProjectDeadline(id, newDeadline, reason) {
    const { data, error } = await supabase.rpc('change_project_deadline', {
      p_project_id: id,
      p_new_deadline: newDeadline || null,
      p_reason: reason,
    });
    if (error) throw error;
    const updated = Array.isArray(data) ? data[0] : data;
    if (updated) setProjects(xs => xs.map(p => p.id === id ? updated : p));
    // Refresh just the audit rows for this project so the new entry shows up.
    const { data: dc } = await supabase
      .from('deadline_changes')
      .select('*')
      .eq('project_id', id)
      .order('changed_at', { ascending: false });
    setDeadlineChanges(xs => [
      ...(dc || []),
      ...xs.filter(c => c.project_id !== id),
    ]);
    return updated;
  }

  // Owner takes a stand on a missed deadline: documents the reason and either
  // reschedules (sets a future deadline) or closes the project — atomically via
  // RPC. Refreshes the overrun log, the project, and (on reschedule) the
  // deadline-change history. Throws 'deadline_reason_required',
  // 'new_deadline_must_be_future', etc. from the RPC.
  async function acknowledgeDeadlineOverrun(projectId, { reason, resolution, newDeadline = null, wasOverBudget = false }) {
    const { data, error } = await supabase.rpc('acknowledge_deadline_overrun', {
      p_project_id: projectId,
      p_reason: reason,
      p_resolution: resolution,
      p_new_deadline: resolution === 'rescheduled' ? (newDeadline || null) : null,
      p_was_over_budget: !!wasOverBudget,
    });
    if (error) throw error;
    const updated = Array.isArray(data) ? data[0] : data;
    if (updated) setProjects(xs => xs.map(p => p.id === projectId ? updated : p));
    // Refresh the overrun rows for this project so the statement shows up.
    const { data: ov } = await supabase
      .from('deadline_overruns')
      .select('*')
      .eq('project_id', projectId)
      .order('acknowledged_at', { ascending: false });
    setDeadlineOverruns(xs => [
      ...(ov || []),
      ...xs.filter(o => o.project_id !== projectId),
    ]);
    // On reschedule the RPC also wrote a deadline_changes audit row — pull it in.
    if (resolution === 'rescheduled') {
      const { data: dc } = await supabase
        .from('deadline_changes')
        .select('*')
        .eq('project_id', projectId)
        .order('changed_at', { ascending: false });
      setDeadlineChanges(xs => [
        ...(dc || []),
        ...xs.filter(c => c.project_id !== projectId),
      ]);
    }
    return updated;
  }

  async function deleteProject(id) {
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) throw error;
    setProjects(xs => xs.filter(p => p.id !== id));
    // cascades on DB: time_entries + project_members are auto-removed
    setTimeEntries(xs => xs.filter(e => e.project_id !== id));
    setProjectMembers(xs => xs.filter(m => m.project_id !== id));
  }

  async function upsertCompletion({ project_id, reason }) {
    const { data, error } = await supabase
      .from('project_completions')
      .upsert({ project_id, user_id: userId, reason }, { onConflict: 'project_id,user_id' })
      .select().single();
    if (error) throw error;
    setCompletions(xs => {
      const without = xs.filter(c => !(c.project_id === project_id && c.user_id === userId));
      return [...without, data];
    });
    return data;
  }

  // Abschluss-Statuswechsel laufen über die RPC aus Migration 25, nicht über
  // ein direktes UPDATE: Die projects-Policy erlaubt Änderungen nur Admins und
  // dem Ersteller, abschließen dürfen aber alle Beteiligten. Die RPC prüft die
  // Beteiligung und — bei Budgetüberzug — ob wirklich jede Begründung vorliegt.
  async function closeProjectAction(projectId, action) {
    const { data, error } = await supabase.rpc('close_project', {
      p_project_id: projectId,
      p_action: action,
    });
    if (error) throw error;
    const updated = Array.isArray(data) ? data[0] : data;
    if (updated) setProjects(xs => xs.map(p => p.id === projectId ? updated : p));
    return updated;
  }

  async function startProjectClosing(projectId) {
    return closeProjectAction(projectId, 'start');
  }

  async function finalizeProject(projectId) {
    // `was_over_budget` setzt die RPC selbst — sie rechnet den Verbrauch nach.
    return closeProjectAction(projectId, 'finalize');
  }

  async function cancelProjectClosing(projectId) {
    return closeProjectAction(projectId, 'cancel');
  }

  async function setProjectStatus(projectId, status) {
    return updateProject(projectId, { status });
  }

  async function addTask({ project_id, title, assignee_id = null, due_date = null }) {
    if (!title?.trim()) return null;
    // Position: append to the end of this project's tasks
    const projectTasks = tasks.filter(t => t.project_id === project_id);
    const nextPos = projectTasks.length > 0 ? Math.max(...projectTasks.map(t => t.position || 0)) + 1 : 0;
    const { data, error } = await supabase.from('tasks').insert({
      project_id, title: title.trim(), position: nextPos,
      assignee_id: assignee_id || null, due_date: due_date || null,
    }).select().single();
    if (error) throw error;
    setTasks(xs => [...xs, data]);
    return data;
  }

  // `items` = Titel-Strings oder { title, assignee_id, due_date }.
  async function addTasksBatch(project_id, items) {
    const valid = (items || [])
      .map(t => (typeof t === 'string' ? { title: t } : t || {}))
      .map(t => ({ ...t, title: t.title?.trim() }))
      .filter(t => t.title);
    if (valid.length === 0) return [];
    const rows = valid.map((t, i) => ({
      project_id, title: t.title, position: i,
      assignee_id: t.assignee_id || null, due_date: t.due_date || null,
    }));
    const { data, error } = await supabase.from('tasks').insert(rows).select();
    if (error) throw error;
    setTasks(xs => [...xs, ...(data || [])]);
    return data || [];
  }

  async function updateTask(id, patch) {
    const { data, error } = await supabase.from('tasks').update(patch).eq('id', id).select().single();
    if (error) throw error;
    setTasks(xs => xs.map(t => t.id === id ? data : t));
    return data;
  }

  async function toggleTaskDone(id, done) {
    const patch = done
      ? { done: true,  done_at: new Date().toISOString(), done_by: userId }
      : { done: false, done_at: null, done_by: null };
    return updateTask(id, patch);
  }

  async function deleteTask(id) {
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (error) throw error;
    setTasks(xs => xs.filter(t => t.id !== id));
  }

  async function addAbsence(input) {
    const { data, error } = await supabase.from('absences').insert(input).select().single();
    if (error) throw error;
    setAbsences(xs => [data, ...xs]);
    return data;
  }

  async function deleteAbsence(id) {
    const { error } = await supabase.from('absences').delete().eq('id', id);
    if (error) throw error;
    setAbsences(xs => xs.filter(a => a.id !== id));
  }

  // Admin-side update of any profile (member's own profile uses updateProfile)
  async function updateAnyProfile(profileId, patch) {
    const { data, error } = await supabase.from('profiles').update(patch).eq('id', profileId).select().single();
    if (error) throw error;
    setProfiles(xs => xs.map(p => p.id === profileId ? data : p));
    return data;
  }

  async function updateProfile(patch) {
    const { data, error } = await supabase.from('profiles').update(patch).eq('id', userId).select().single();
    if (error) throw error;
    setProfiles(xs => xs.map(p => p.id === userId ? data : p));
    return data;
  }

  async function assignProjectMembers(projectId, userIds) {
    await supabase.from('project_members').delete().eq('project_id', projectId);
    const rows = userIds.map(user_id => ({ project_id: projectId, user_id }));
    if (rows.length > 0) {
      const { error } = await supabase.from('project_members').insert(rows);
      if (error) throw error;
    }
    setProjectMembers(xs => [...xs.filter(m => m.project_id !== projectId), ...rows]);
  }

  // Admin sends feedback to one or more members. `rows` items:
  // { to_user_id, project_id?, category, message, context? }.
  async function addFeedback(rows) {
    const payload = (Array.isArray(rows) ? rows : [rows]).map(r => ({
      to_user_id: r.to_user_id,
      from_user_id: userId,
      project_id: r.project_id || null,
      category: r.category || 'general',
      message: r.message,
      context: r.context || null,
    }));
    const { data, error } = await supabase.from('feedback').insert(payload).select();
    if (error) throw error;
    setFeedback(xs => [...(data || []), ...xs]);
    return data || [];
  }

  // --- @-Mentions -----------------------------------------------------------

  // Detect @-mentions in a text field on save and notify newly-mentioned users.
  // `prevText` lets edits only ping people who were just added (not re-ping
  // everyone on every save). Self-mentions are ignored. In-app is authoritative;
  // the e-mail is fire-and-forget and no-ops until the edge function is set up.
  async function syncMentions({ text, prevText = '', sourceType, sourceId = null, projectId = null }) {
    const added = extractMentionIds(text)
      .filter(id => id !== userId && !extractMentionIds(prevText).includes(id));
    if (added.length === 0) return [];

    const snippet = mentionSnippet(text);
    const payload = added.map(id => ({
      to_user_id: id,
      from_user_id: userId,
      source_type: sourceType,
      source_id: sourceId,
      project_id: projectId,
      snippet,
    }));
    const { data, error } = await supabase.from('mentions').insert(payload).select();
    if (error) {
      // Never let a mention failure block the primary save — just log it.
      console.warn('[mentions] insert failed', error.message);
      return [];
    }
    setMentions(xs => [...(data || []), ...xs]);

    // E-Mail-Benachrichtigung anstoßen (best effort — braucht die Edge Function
    // `notify-mention` + RESEND_API_KEY; ohne die passiert schlicht nichts).
    if (data?.length) {
      supabase.functions
        .invoke('notify-mention', { body: { mentionIds: data.map(m => m.id) } })
        .catch(() => {});
    }
    return data || [];
  }

  // Mark a single mention row read (recipient action). Optimistic.
  async function markMentionRead(id) {
    const target = mentions.find(m => m.id === id);
    if (!target || target.read_at) return;
    const readAt = new Date().toISOString();
    setMentions(xs => xs.map(m => m.id === id ? { ...m, read_at: readAt } : m));
    const { error } = await supabase.from('mentions').update({ read_at: readAt }).eq('id', id);
    if (error) {
      setMentions(xs => xs.map(m => m.id === id ? { ...m, read_at: null } : m));
      throw error;
    }
  }

  // Mark a single feedback row read (recipient action). Optimistic.
  async function markFeedbackRead(id) {
    const target = feedback.find(f => f.id === id);
    if (!target || target.read_at) return;
    const readAt = new Date().toISOString();
    setFeedback(xs => xs.map(f => f.id === id ? { ...f, read_at: readAt } : f));
    const { error } = await supabase.from('feedback').update({ read_at: readAt }).eq('id', id);
    if (error) {
      setFeedback(xs => xs.map(f => f.id === id ? { ...f, read_at: null } : f));
      throw error;
    }
  }

  // --- Goal mutations -------------------------------------------------------

  // Create or update the single goal of a customer (admin). Upsert on customer_id.
  // Metrics are managed separately via saveMetrics.
  async function upsertGoal(customerId, fields) {
    const existing = goalByCustomer[customerId];
    const payload = {
      customer_id: customerId,
      title: fields.title,
      start_date: fields.start_date,
      target_date: fields.target_date,
    };
    if (existing) {
      const { data, error } = await supabase.from('customer_goals').update(payload).eq('id', existing.id).select().single();
      if (error) throw error;
      setGoals(xs => xs.map(g => g.id === data.id ? data : g));
      return data;
    }
    const { data, error } = await supabase.from('customer_goals').insert({ ...payload, created_by: userId }).select().single();
    if (error) throw error;
    setGoals(xs => [...xs, data]);
    return data;
  }

  async function deleteGoal(goalId) {
    const { error } = await supabase.from('customer_goals').delete().eq('id', goalId);
    if (error) throw error;
    setGoals(xs => xs.filter(g => g.id !== goalId));
    // DB cascades metrics → progress, plus checkins — mirror locally.
    const metricIds = new Set(metrics.filter(m => m.goal_id === goalId).map(m => m.id));
    setMetrics(xs => xs.filter(m => m.goal_id !== goalId));
    setGoalProgress(xs => xs.filter(p => !metricIds.has(p.metric_id)));
    setCheckins(xs => xs.filter(c => c.goal_id !== goalId));
  }

  // Replace the criteria (metrics) of a goal (admin). Keeps existing metric ids
  // (so their progress survives), inserts new ones, deletes removed ones.
  // `rows` items: { id?, label, unit, type, start_value, target_value, cadence, quota }.
  async function saveMetrics(goalId, rows) {
    const existing = metrics.filter(m => m.goal_id === goalId);
    const keepIds = new Set((rows || []).filter(r => r.id).map(r => r.id));
    const toDelete = existing.filter(m => !keepIds.has(m.id));
    if (toDelete.length > 0) {
      const ids = toDelete.map(m => m.id);
      const { error } = await supabase.from('goal_metrics').delete().in('id', ids);
      if (error) throw error;
      const delSet = new Set(ids);
      setGoalProgress(xs => xs.filter(p => !delSet.has(p.metric_id)));
    }
    const payload = (rows || []).map((r, i) => ({
      ...(r.id ? { id: r.id } : {}),
      goal_id: goalId,
      label: (r.label || '').trim(),
      unit: r.unit?.trim() || null,
      type: r.type === 'recurring' ? 'recurring' : 'cumulative',
      start_value: r.type === 'recurring' ? null : (Number(r.start_value) || 0),
      target_value: r.type === 'recurring' ? null : (Number(r.target_value) || 0),
      ramp: r.ramp === 'exponential' && r.type !== 'recurring' ? 'exponential' : 'linear',
      cadence: r.type === 'recurring' ? (r.cadence || 'monthly') : null,
      quota: r.type === 'recurring' ? (Number(r.quota) || 0) : null,
      position: i,
    }));
    let saved = [];
    if (payload.length > 0) {
      const { data, error } = await supabase.from('goal_metrics').upsert(payload).select();
      if (error) throw error;
      saved = data || [];
    }
    setMetrics(xs => [...xs.filter(m => m.goal_id !== goalId), ...saved]);
    return saved;
  }

  // Update the shared actual value of a metric in a given month (last write wins).
  async function updateGoalProgress(metricId, periodMonth, value) {
    const { data, error } = await supabase
      .from('goal_progress')
      .upsert(
        { metric_id: metricId, period_month: periodMonth, value: Number(value), updated_by: userId, updated_at: new Date().toISOString() },
        { onConflict: 'metric_id,period_month' }
      )
      .select().single();
    if (error) throw error;
    setGoalProgress(xs => {
      const without = xs.filter(p => !(p.metric_id === metricId && p.period_month === periodMonth));
      return [...without, data];
    });
    return data;
  }

  // Submit a member's monthly check-in. Persists the personal reflection AND the
  // reported per-metric values (shared progress). `values` = [{ metric_id, value }].
  async function submitCheckin(goalId, periodMonth, { values, reflection, confidence }) {
    for (const v of (values || [])) {
      if (v.value != null && v.value !== '') {
        await updateGoalProgress(v.metric_id, periodMonth, v.value);
      }
    }
    const { data, error } = await supabase
      .from('goal_checkins')
      .upsert(
        {
          goal_id: goalId,
          user_id: userId,
          period_month: periodMonth,
          reflection: reflection.trim(),
          confidence: confidence || 'on_track',
        },
        { onConflict: 'goal_id,user_id,period_month' }
      )
      .select().single();
    if (error) throw error;
    setCheckins(xs => {
      const without = xs.filter(c => !(c.goal_id === goalId && c.user_id === userId && c.period_month === periodMonth));
      return [data, ...without];
    });
    return data;
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const value = {
    session,
    currentProfile,
    isAdmin,
    profiles,
    customers,
    projects,
    timeEntries,
    projectMembers,
    membersByProject,
    involvedByProject,
    completionsByProject,
    completions,
    absences,
    tasks,
    deadlineChanges,
    deadlineOverruns,
    overrunsByProject,
    overrunForCurrentDeadline,
    dueDeadlineOverruns,
    dueMissingTimeDays,
    feedback,
    myFeedback,
    unreadFeedbackCount,
    mentions,
    myMentions,
    unreadMentionsCount,
    goals,
    metrics,
    goalProgress,
    checkins,
    goalByCustomer,
    goalForProject,
    goalMembersByCustomer,
    metricsByGoal,
    progressByMetric,
    checkinsByGoal,
    dueGoalCheckins,
    projectUsage,
    todaysEntries,
    gamification,
    gamificationByUser,
    loading,
    error,
    projectById,
    customerById,
    profileById,
    refetch: load,
    addTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    shareTimeEntry,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addProject,
    syncDriveFolders,
    updateProject,
    changeProjectDeadline,
    acknowledgeDeadlineOverrun,
    deleteProject,
    assignProjectMembers,
    upsertCompletion,
    startProjectClosing,
    finalizeProject,
    cancelProjectClosing,
    setProjectStatus,
    updateProfile,
    updateAnyProfile,
    addAbsence,
    deleteAbsence,
    addTask,
    addTasksBatch,
    updateTask,
    toggleTaskDone,
    deleteTask,
    addFeedback,
    markFeedbackRead,
    syncMentions,
    markMentionRead,
    upsertGoal,
    deleteGoal,
    saveMetrics,
    updateGoalProgress,
    submitCheckin,
    signOut,
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: '#0B0D13', color: 'var(--ink-3)', fontFamily: 'var(--sans)',
      }}>
        <div className="tiny muted">Lade Daten…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: '#0B0D13', color: 'var(--ink)', fontFamily: 'var(--sans)',
      }}>
        <div style={{ maxWidth: 420, padding: 24, textAlign: 'center' }}>
          <div style={{ color: 'var(--crit)', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Fehler beim Laden</div>
          <div className="tiny muted" style={{ marginBottom: 16 }}>{error}</div>
          <button className="btn btn-primary" onClick={load}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
